import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from '../../assets';
import type {
  BrandsFilters,
  CampaignFilters,
  ControlPiecesFilters,
  InfluencersFilters,
  InfluentiaUsersFilters,
  InvoicesFilters,
} from '../../types';
import { formatNumberMoney } from '../../utils/numberFormatter';

type SingleFilterProps = {
  title: string;
  value: string;
  removeFilter: () => void;
};

const SingleFilter = ({ title, value, removeFilter }: SingleFilterProps) => {
  return (
    <div className="flex px-2 rounded-md flex-row gap-x-2 text-sm bg-influentia-light-blue text-dark-blue items-center justify-center font-medium">
      <span className="capitalize">
        {title}
        {': '}
        {value}
      </span>
      <button onClick={removeFilter}>
        <CloseIcon />
      </button>
    </div>
  );
};

type AllFilters = CampaignFilters &
  InfluencersFilters &
  BrandsFilters &
  InfluentiaUsersFilters &
  InvoicesFilters &
  ControlPiecesFilters;

type ActiveFiltersProps = {
  setFilters: (filters: any) => void;
  filters: AllFilters;
  type?: string;
};

const EXCLUDE = ['orderDirection', 'orderBy'];
const PAIR_FIELDS = [['from_month', 'to_month']];
const HDIE_FILTERS = [
  'name',
  'campaignName',
  'campaign_name',
  'account_manager_campaigns',
  'has_publication_date',
  'month_from',
  'month_to',
  'grouped',
];

export const ActiveFilters = ({
  setFilters,
  filters,
  type,
}: ActiveFiltersProps) => {
  const { t } = useTranslation('common');
  const formatValue = (key: keyof AllFilters) => {
    switch (key) {
      case 'status':
        return filters[key]?.map((s) => t(`${type}Status.${s}`)).join(', ');
      case 'platforms':
        return filters[key]?.join(', ');
      case 'actionTypes':
        return filters[key]?.map((v) => t(`actionType.${v}`)).join(', ');
      case 'sector':
        return filters[key]?.map((v) => t(`enums.sector.${v}`)).join(', ');
      case 'category':
        return filters[key]?.map((v) => t(`enums.category.${v}`)).join(', ');
      case 'fromMoney':
      case 'toMoney':
      case 'minPrice':
      case 'maxPrice':
        return formatNumberMoney(filters[key] as number);
      case 'fromBilling':
      case 'toBilling':
      case 'fromPayment':
      case 'toPayment':
        return dayjs(filters[key]).format('DD/MM/YYYY');
      case 'client':
      case 'influencers':
        return filters[key]?.map((i) => i.label).join(', ');
      case 'accountManagerCampaigns':
        return t(`global.${filters[key]}`);
      default:
        return filters[key] as string;
    }
  };
  const hasValue = (key: keyof AllFilters) => {
    if (EXCLUDE.includes(key)) return false;
    const f: any = { ...filters };
    switch (key) {
      case 'platforms':
      case 'actionTypes':
      case 'status':
      case 'sector':
      case 'category':
        return !!(f[key] && f[key].length);
      default:
        return !!filters[key];
    }
  };

  const removeFilter = (key: string) => {
    if (PAIR_FIELDS.some((pair) => pair.includes(key))) {
      const pair = PAIR_FIELDS.find((pairs) => pairs.includes(key));
      if (!pair) return;
      const newFilters = { ...filters };
      pair.forEach((p) => {
        newFilters[p as keyof AllFilters] = undefined;
      });
      setFilters(newFilters);
      return;
    }
    const newFilters = { ...filters };
    newFilters[key as keyof AllFilters] = undefined;
    setFilters(newFilters);
  };

  return (
    <div className="flex flex-row gap-x-2">
      {Object.keys(filters).map((k) => (
        <React.Fragment key={k + Math.random()}>
          {hasValue(k as keyof AllFilters) && !HDIE_FILTERS.includes(k) && (
            <SingleFilter
              removeFilter={() => removeFilter(k)}
              title={t(`filters.${k}`)}
              value={formatValue(k as keyof AllFilters) as string}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
